import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const NotificationPanel = ({ isOpen = false, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');

  // Simulate real-time notifications
  useEffect(() => {
    const initialNotifications = [
      {
        id: 1,
        type: 'warning',
        title: 'Asset Audit Due',
        message: 'Quarterly audit for Building A assets is due in 3 days',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        read: false,
        category: 'audit'
      },
      {
        id: 2,
        type: 'success',
        title: 'Warranty Claim Processed',
        message: 'Warranty claim #WC-2025-001 has been successfully processed',
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
        read: false,
        category: 'warranty'
      },
      {
        id: 3,
        type: 'error',
        title: 'Asset Location Mismatch',
        message: 'Asset #AT-2025-0156 location does not match physical scan',
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
        read: true,
        category: 'location'
      },
      {
        id: 4,
        type: 'info',
        title: 'System Maintenance',
        message: 'Scheduled maintenance window: Aug 20, 2025 2:00 AM - 4:00 AM',
        timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
        read: false,
        category: 'system'
      },
      {
        id: 5,
        type: 'warning',
        title: 'License Expiration',
        message: 'Software license for Adobe Creative Suite expires in 30 days',
        timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000),
        read: false,
        category: 'license'
      }
    ];

    setNotifications(initialNotifications);
  }, []);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'error':
        return 'AlertCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'success':
        return 'CheckCircle';
      case 'info':
      default:
        return 'Info';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'error':
        return 'text-error';
      case 'warning':
        return 'text-warning';
      case 'success':
        return 'text-success';
      case 'info':
      default:
        return 'text-accent';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return `${days}d ago`;
    }
  };

  const filteredNotifications = notifications?.filter(notification => {
    if (filter === 'unread') return !notification?.read;
    if (filter === 'read') return notification?.read;
    return true;
  });

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  const markAsRead = (id) => {
    setNotifications(prev =>
      prev?.map(notification =>
        notification?.id === id
          ? { ...notification, read: true }
          : notification
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev?.map(notification => ({ ...notification, read: true }))
    );
  };

  const clearNotification = (id) => {
    setNotifications(prev => prev?.filter(notification => notification?.id !== id));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:relative lg:inset-auto">
      {/* Mobile Overlay */}
      <div 
        className="fixed inset-0 bg-black/20 lg:hidden" 
        onClick={onClose}
      />
      {/* Panel */}
      <div className="fixed right-0 top-16 bottom-0 w-full max-w-md bg-card border-l border-border shadow-elevated lg:absolute lg:top-0 lg:right-0 lg:w-96 lg:max-h-96 lg:rounded-lg lg:border lg:shadow-elevated">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <h3 className="font-semibold text-card-foreground">Notifications</h3>
            {unreadCount > 0 && (
              <span className="bg-error text-error-foreground text-xs px-2 py-1 rounded-full font-medium">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-xs"
              >
                Mark all read
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8"
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex border-b border-border">
          {[
            { key: 'all', label: 'All' },
            { key: 'unread', label: 'Unread' },
            { key: 'read', label: 'Read' }
          ]?.map((tab) => (
            <button
              key={tab?.key}
              onClick={() => setFilter(tab?.key)}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-smooth ${
                filter === tab?.key
                  ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab?.label}
              {tab?.key === 'unread' && unreadCount > 0 && (
                <span className="ml-1 text-xs">({unreadCount})</span>
              )}
            </button>
          ))}
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto max-h-80">
          {filteredNotifications?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Icon name="Bell" size={32} className="text-muted-foreground mb-2" />
              <p className="text-muted-foreground">
                {filter === 'unread' ? 'No unread notifications' : 'No notifications'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredNotifications?.map((notification) => (
                <div
                  key={notification?.id}
                  className={`p-4 hover:bg-muted/50 transition-smooth ${
                    !notification?.read ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`mt-0.5 ${getTypeColor(notification?.type)}`}>
                      <Icon name={getTypeIcon(notification?.type)} size={16} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className={`text-sm font-medium ${
                            !notification?.read ? 'text-card-foreground' : 'text-muted-foreground'
                          }`}>
                            {notification?.title}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {notification?.message}
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {formatTimestamp(notification?.timestamp)}
                          </p>
                        </div>
                        
                        <div className="flex items-center space-x-1 ml-2">
                          {!notification?.read && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => markAsRead(notification?.id)}
                              className="h-6 w-6"
                              title="Mark as read"
                            >
                              <Icon name="Check" size={12} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => clearNotification(notification?.id)}
                            className="h-6 w-6 text-muted-foreground hover:text-error"
                            title="Remove notification"
                          >
                            <Icon name="X" size={12} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {filteredNotifications?.length > 0 && (
          <div className="p-4 border-t border-border">
            <Button variant="outline" size="sm" className="w-full">
              View All Notifications
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;